/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Inline Action Alert
 * Reusable inline alert with optional primary/secondary actions — parent controls message and buttons.
 *
 * @file InlineActionAlert.jsx
 * @author Anuja Gaikwad
 * @since 2.0.3
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button, IconButton, Typography } from '@mui/material';
import mixins from '../../assets/styles/mixins.scss';
import Icon from '../icons/Icon';
import BannerCloseIcon from '../icons/BannerCloseIcon';
import SafeButtonWrapper from './SafeButtonWrapper';
import { InlineActionAlertStylesRoot, classes } from './inlineActionAlertStyles';
import { INLINE_ACTION_ALERT_FEEDBACK_VARIANT } from '../../common/constants';

const shouldShowButton = (buttonConfig) => (
  Boolean(buttonConfig?.label) && buttonConfig.show !== false
);

const FEEDBACK_ICON_BY_VARIANT = {
  /** Student temp access warning banner — uses feedbackIcon + feedbackIconWarning. */
  [INLINE_ACTION_ALERT_FEEDBACK_VARIANT.WARNING]: 'BannerWarningIcon',
  /** Student temp access alert banner — uses feedbackIcon + feedbackIconAlert. */
  [INLINE_ACTION_ALERT_FEEDBACK_VARIANT.ALERT]: 'AlertIcon'
};

const InlineActionAlert = ({
  message,
  accentColor,
  feedbackVariant,
  onClose,
  primaryButton,
  secondaryButton,
  className
}) => {
  const useFeedbackLayout = Boolean(feedbackVariant);

  const renderButton = (buttonConfig, buttonClass, isPrimary = false) => {
    if (!shouldShowButton(buttonConfig)) {
      return null;
    }

    return (
      <SafeButtonWrapper isLoading={buttonConfig.isLoading}>
        <Button
          type="button"
          variant={isPrimary ? 'contained' : undefined}
          className={buttonClass}
          onClick={buttonConfig.onClick}
          disabled={buttonConfig.disabled}
        >
          {buttonConfig.label}
        </Button>
      </SafeButtonWrapper>
    );
  };

  const renderActions = (inline = false) => {
    const hasActions = shouldShowButton(primaryButton) || shouldShowButton(secondaryButton);

    if (!hasActions) {
      return null;
    }

    return (
      <div className={`${classes.actions} ${inline ? classes.actionsInline : ''}`}>
        {renderButton(secondaryButton, classes.secondaryButton)}
        {renderButton(primaryButton, classes.primaryButton, true)}
      </div>
    );
  };

  const renderCloseButton = (hasActions) => {
    if (!onClose) {
      return null;
    }

    return (
      <IconButton
        className={`${classes.closeButton} ${hasActions ? '' : classes.closeButtonNoCta}`}
        onClick={onClose}
        aria-label="Dismiss alert"
        disableRipple
      >
        <BannerCloseIcon className={classes.closeIcon} />
      </IconButton>
    );
  };

  const renderFeedbackLayout = () => {
    const hasActions = shouldShowButton(primaryButton) || shouldShowButton(secondaryButton);
    const highlightClass = feedbackVariant === INLINE_ACTION_ALERT_FEEDBACK_VARIANT.WARNING
      ? classes.highlightWarning
      : classes.highlightAlert;
    const feedbackIconClass = feedbackVariant === INLINE_ACTION_ALERT_FEEDBACK_VARIANT.WARNING
      ? classes.feedbackIconWarning
      : classes.feedbackIconAlert;

    return (
      <InlineActionAlertStylesRoot
        className={`${classes.rootFeedback} ${className || ''}`}
        role="status"
      >
        <div className={`${classes.highlight} ${highlightClass}`} aria-hidden="true" />
        <div
          className={`${classes.feedbackContent} ${onClose ? classes.feedbackContentWithClose : ''}`}
        >
          <div className={classes.messageLeading}>
            <div className={classes.iconWrapper}>
              <Icon
                name={FEEDBACK_ICON_BY_VARIANT[feedbackVariant]}
                className={`${classes.feedbackIcon} ${feedbackIconClass}`}
              />
            </div>
            <Typography component="p" className={`${classes.message} ${classes.messageFeedback}`}>
              {message}
            </Typography>
          </div>
          {hasActions && (
            <div className={classes.feedbackMain}>
              {renderActions(true)}
            </div>
          )}
          {renderCloseButton(hasActions)}
        </div>
      </InlineActionAlertStylesRoot>
    );
  };

  const renderDefaultLayout = () => (
    <InlineActionAlertStylesRoot className={`${classes.root} ${className || ''}`} role="status">
      <div className={classes.content}>
        <div
          className={classes.accent}
          style={{ backgroundColor: accentColor }}
          aria-hidden="true"
        />
        <Typography component="p" className={classes.message}>
          {message}
        </Typography>
      </div>
      {renderActions()}
    </InlineActionAlertStylesRoot>
  );

  if (!message) {
    return null;
  }

  return useFeedbackLayout ? renderFeedbackLayout() : renderDefaultLayout();
};

const buttonConfigPropType = PropTypes.shape({
  label: PropTypes.string,
  onClick: PropTypes.func,
  show: PropTypes.bool,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool
});

InlineActionAlert.propTypes = {
  message: PropTypes.string,
  accentColor: PropTypes.string,
  feedbackVariant: PropTypes.oneOf(Object.values(INLINE_ACTION_ALERT_FEEDBACK_VARIANT)),
  onClose: PropTypes.func,
  primaryButton: buttonConfigPropType,
  secondaryButton: buttonConfigPropType,
  className: PropTypes.string
};

InlineActionAlert.defaultProps = {
  message: '',
  accentColor: mixins.linkDefault,
  feedbackVariant: '',
  onClose: null,
  primaryButton: null,
  secondaryButton: null,
  className: ''
};

export default InlineActionAlert;
